// Step 3 — Your Info.
//
// Collects the consumer's contact details + budget/timeline/source + optional
// photos + optional booking + GDPR consent. Validation is inline on the Next
// click (so users see exactly what's missing). Photos are stored as data
// URLs in memory — submit.ts forwards them to the backend.

import type {
  BookingType, BudgetChoice, CustomerInfo, LeadSourceChoice, PhotoUpload, TimelineChoice,
} from "../types";
import type { StepContext } from "./index";
import { escape, escapeAttr } from "./index";
import { track } from "./analytics";

const BUDGETS: BudgetChoice[] = ["Under £5k", "£5k-£10k", "£10k-£15k", "£15k-£20k", "£20k+"];
const TIMELINES: TimelineChoice[] = ["ASAP", "1-3 months", "3-6 months", "6-12 months", "Just browsing"];
const SOURCES: LeadSourceChoice[] = ["Google", "Social media", "Word of mouth", "Existing customer", "Supplier recommendation", "Other"];

// The appointment types the wizard can offer. Which of these actually appear is
// controlled per-retailer via branding.appointmentTypes (migration 0072);
// default is all-on. `needsDateTime` is false for "Request a Call" — there is
// no visit to schedule — so the date/time fields stay hidden for it.
//
// 2026-08-12 (Steve): reduced to three.
//   • "Remote / Video Consultation" folded into "Request a Call" — the retailer
//     decides phone vs video when they actually ring.
//   • "Supply Only" removed. It was the wizard's only capture of supply-only
//     intent, but already a weak one: the API coerces it to
//     `design_consultation` for the PG booking_type CHECK, so it never reached
//     Postgres as itself. The authoritative flag is `leads.supply_type`, which
//     the retailer sets on the customer record. Supply-only customers now come
//     through as a call request.
// `remote` and `supply-only` stay in the BookingType union and in the retailer
// settings toggles so historical leads still render their real label.
type OfferedBookingType = Exclude<BookingType, "">;
const APPOINTMENT_TYPES: Array<{ value: OfferedBookingType; label: string; icon: string; needsDateTime: boolean }> = [
  { value: "showroom",   label: "Showroom Visit",    icon: "⌂", needsDateTime: true },
  { value: "home-visit", label: "Home Design Visit", icon: "⌂", needsDateTime: true },
  { value: "callback",   label: "Request a Call",    icon: "☎", needsDateTime: false },
];

/** Does the given booking type need date/time fields? Unknown/empty → false. */
function bookingNeedsDateTime(value: string): boolean {
  return APPOINTMENT_TYPES.some((t) => t.value === value && t.needsDateTime);
}

// "Request a call" books no visit, so the card retitles itself to what actually
// happens next: someone rings them. Everything else keeps the appointment
// wording. (`supply-only` kept here so a part-completed session restored with
// the old value still reads correctly.)
function noVisitBooking(value: string): boolean {
  return value === "callback" || value === "supply-only";
}

function bookingHeading(value: string): string {
  return noVisitBooking(value) ? "Request a Call Back" : "Book an Appointment";
}

function bookingSubtitle(value: string): string {
  return noVisitBooking(value)
    ? "We'll give you a call to talk through what you need."
    : "Would you like to arrange an appointment with your specialist?";
}

const MAX_PHOTOS = 6;
const MAX_PHOTO_BYTES = 5 * 1024 * 1024;
const ACCEPTED_IMAGE = /^image\//;

export function mountStep3(host: HTMLElement, ctx: StepContext): void {
  // Per-retailer appointment types: show a type unless explicitly disabled.
  const apptCfg = ctx.state.branding.appointmentTypes;
  const offeredTypes = APPOINTMENT_TYPES.filter((t) => !apptCfg || apptCfg[t.value] !== false);
  const selected = ctx.state.booking.bookingType;
  // The booking card is only rendered when the retailer offers at least one
  // type. When hidden it carries no UI — booking stays optional and empty.
  const bookingCardHtml = offeredTypes.length === 0 ? "" : `
    <div class="wiz-card">
      <h3 class="wiz-card__title" data-role="booking-title">${escape(bookingHeading(selected))} <span class="wiz-field__hint">(optional)</span></h3>
      <p class="wiz-card__subtitle" data-role="booking-sub">${escape(bookingSubtitle(selected))}</p>
      <div class="wiz-booking-types" data-role="booking-types">
        ${offeredTypes.map((b) => `
          <button type="button" class="wiz-booking-type ${selected === b.value ? "wiz-booking-type--on" : ""}"
                  data-booking-type="${b.value}">
            <span class="wiz-booking-type__icon" aria-hidden="true">${b.icon}</span>
            ${escape(b.label)}
          </button>
        `).join("")}
      </div>
      <div class="wiz-row" data-role="booking-fields" style="margin-top:0.85rem;${selected && bookingNeedsDateTime(selected) ? "" : "display:none;"}">
        <div class="wiz-field">
          <label class="wiz-field__label" for="wiz-date">Preferred date</label>
          <input class="wiz-input" id="wiz-date" type="date" min="${todayIso()}"
                 value="${escapeAttr(ctx.state.booking.preferredDate)}" />
        </div>
        <div class="wiz-field">
          <label class="wiz-field__label" for="wiz-time">Preferred time</label>
          <select class="wiz-select" id="wiz-time">
            <option value="" ${ctx.state.booking.preferredTime === "" ? "selected" : ""}>Any time</option>
            <option value="morning" ${ctx.state.booking.preferredTime === "morning" ? "selected" : ""}>Morning (9am-12pm)</option>
            <option value="afternoon" ${ctx.state.booking.preferredTime === "afternoon" ? "selected" : ""}>Afternoon (12pm-5pm)</option>
            <option value="evening" ${ctx.state.booking.preferredTime === "evening" ? "selected" : ""}>Evening (5pm-7pm)</option>
          </select>
        </div>
      </div>
    </div>`;
  host.innerHTML = `
    <div class="wiz-card">
      <h2 class="wiz-card__title">Your details</h2>
      <p class="wiz-card__subtitle">
        We need a way to reach you. Your specialist will be in touch about your
        dream kitchen — no salesman pressure, just expert help.
      </p>

      <div class="wiz-row">
        <div class="wiz-field">
          <label class="wiz-field__label" for="wiz-firstname">First name *</label>
          <input class="wiz-input" id="wiz-firstname" type="text" autocomplete="given-name"
                 value="${escapeAttr(ctx.state.customer.firstName)}" required />
          <div class="wiz-field__error" data-err="firstName"></div>
        </div>
        <div class="wiz-field">
          <label class="wiz-field__label" for="wiz-lastname">Last name *</label>
          <input class="wiz-input" id="wiz-lastname" type="text" autocomplete="family-name"
                 value="${escapeAttr(ctx.state.customer.lastName)}" required />
          <div class="wiz-field__error" data-err="lastName"></div>
        </div>
      </div>

      <div class="wiz-field">
        <label class="wiz-field__label" for="wiz-email">Email *</label>
        <input class="wiz-input" id="wiz-email" type="email" autocomplete="email" inputmode="email"
               value="${escapeAttr(ctx.state.customer.email)}" required />
        <div class="wiz-field__error" data-err="email"></div>
      </div>

      <div class="wiz-field">
        <label class="wiz-field__label" for="wiz-phone">Phone *</label>
        <input class="wiz-input" id="wiz-phone" type="tel" autocomplete="tel" inputmode="tel"
               value="${escapeAttr(ctx.state.customer.phone)}" required />
        <div class="wiz-field__error" data-err="phone"></div>
      </div>

      <!-- Postcode first, then look the address up from it: fewer keystrokes
           than a free-text street box, and the result is a real, deliverable
           address rather than whatever the customer typed. Manual entry stays
           one click away and takes over automatically if lookup is unavailable. -->
      <div class="wiz-field">
        <label class="wiz-field__label" for="wiz-postcode">Postcode *</label>
        <div class="wiz-lookup">
          <input class="wiz-input" id="wiz-postcode" type="text" autocomplete="postal-code"
                 inputmode="text" maxlength="10" placeholder="e.g. HG1 1AA"
                 value="${escapeAttr(ctx.state.customer.postcode)}" required />
          <button type="button" class="wiz-lookup__btn" data-role="find-address">Find address</button>
        </div>
        <div class="wiz-field__error" data-err="postcode"></div>
      </div>

      <div class="wiz-field" data-role="address-results-field" hidden>
        <label class="wiz-field__label" for="wiz-address-select">Select your address</label>
        <select class="wiz-input" id="wiz-address-select" data-role="address-results"></select>
      </div>

      <div class="wiz-field" data-role="address-manual-field"${ctx.state.customer.address ? "" : " hidden"}>
        <label class="wiz-field__label" for="wiz-address">Address *</label>
        <input class="wiz-input" id="wiz-address" type="text" autocomplete="street-address"
               placeholder="Street address"
               value="${escapeAttr(ctx.state.customer.address)}" required />
        <!-- Deliberately neutral. Anything about delivery or planning implies a
             commitment the customer hasn't made — at this point they're just
             making first contact with the retailer. -->
        <p class="wiz-field__hint">So we know the property.</p>
        <div class="wiz-field__error" data-err="address"></div>
      </div>

      <p class="wiz-lookup__status" data-role="address-status"></p>
      <button type="button" class="wiz-lookup__manual" data-role="address-manual-toggle">
        Enter my address manually
      </button>
    </div>

    <div class="wiz-card">
      <h3 class="wiz-card__title">Budget</h3>
      <p class="wiz-card__subtitle">Helps us recommend options that fit.</p>
      <div class="wiz-toggle-group" data-role="budget">
        ${BUDGETS.map((b) => pill(b, ctx.state.project.budget === b)).join("")}
      </div>
    </div>

    <div class="wiz-card">
      <h3 class="wiz-card__title">When are you looking to start?</h3>
      <div class="wiz-toggle-group" data-role="timeline">
        ${TIMELINES.map((t) => pill(t, ctx.state.project.timeline === t)).join("")}
      </div>
    </div>

    <div class="wiz-card">
      <h3 class="wiz-card__title">How did you find us?</h3>
      <div class="wiz-toggle-group" data-role="source">
        ${SOURCES.map((s) => pill(s, ctx.state.project.leadSource === s)).join("")}
      </div>
    </div>

    <div class="wiz-card">
      <h3 class="wiz-card__title">Photos of your current kitchen <span class="wiz-field__hint">(optional)</span></h3>
      <p class="wiz-card__subtitle">Up to ${MAX_PHOTOS} photos, max 5MB each. Helps your designer understand the space.</p>
      <label class="wiz-photo-drop" data-role="photo-drop">
        <input type="file" accept="image/*" multiple data-role="photo-input" />
        <span>📷 Tap to add photos, or drop them here</span>
      </label>
      <div class="wiz-photo-grid" data-role="photo-grid"></div>
      <div class="wiz-field__error" data-err="photos"></div>
    </div>

    ${bookingCardHtml}

    <div class="wiz-card">
      <label class="wiz-toggle" data-role="gdpr" style="border:none;background:transparent;padding:0;align-items:flex-start;">
        <input type="checkbox" style="margin-right:0.5rem;width:18px;height:18px;position:static;opacity:1;pointer-events:auto;"
               ${ctx.state.project.gdprConsent ? "checked" : ""} />
        <span style="font-weight:400;font-size:0.875rem;line-height:1.5;color:var(--text);">
          I agree to be contacted about my kitchen enquiry. See our
          <a href="/privacy" target="_blank" rel="noopener">privacy policy</a>. *
        </span>
      </label>
      <div class="wiz-field__error" data-err="gdpr"></div>
    </div>

    <div class="wiz-footer">
      <button class="wiz-btn wiz-btn--secondary" data-action="back">← Back</button>
      <button class="wiz-btn" data-action="next">Next: Review →</button>
    </div>
  `;

  // ── Wire customer fields ──
  bindText(host, "#wiz-firstname", (v) => updateCustomer(ctx, { firstName: v }));
  bindText(host, "#wiz-lastname",  (v) => updateCustomer(ctx, { lastName:  v }));
  bindText(host, "#wiz-email",     (v) => updateCustomer(ctx, { email:     v.trim() }));
  bindText(host, "#wiz-phone",     (v) => updateCustomer(ctx, { phone:     v.trim() }));
  bindText(host, "#wiz-address",   (v) => updateCustomer(ctx, { address:   v.trim() }));
  bindText(host, "#wiz-postcode",  (v) => updateCustomer(ctx, { postcode:  v.trim().toUpperCase() }));

  wireAddressLookup(host, ctx);

  // ── Pill groups (budget/timeline/source) ──
  wirePillGroup(host, "[data-role=budget]",   (v) => {
    setProject(ctx, { budget: v as BudgetChoice });
    if (v) track({ eventType: "budget_selected", value: v });
  });
  wirePillGroup(host, "[data-role=timeline]", (v) => {
    setProject(ctx, { timeline: v as TimelineChoice });
    if (v) track({ eventType: "timeline_selected", value: v });
  });
  wirePillGroup(host, "[data-role=source]",   (v) => setProject(ctx, { leadSource: v as LeadSourceChoice }));

  // ── Photo upload ──
  wirePhotos(host, ctx);

  // ── Booking ── (only wired when the retailer offers at least one type)
  const bookingTypesEl = host.querySelector<HTMLElement>("[data-role=booking-types]");
  const bookingFields = host.querySelector<HTMLElement>("[data-role=booking-fields]");
  if (bookingTypesEl && bookingFields) {
  bookingTypesEl.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>("[data-booking-type]");
    if (!btn) return;
    const value = btn.dataset.bookingType as BookingType;
    const newValue = ctx.state.booking.bookingType === value ? "" : value;
    ctx.setState({ ...ctx.state, booking: { ...ctx.state.booking, bookingType: newValue } });
    host.querySelectorAll<HTMLButtonElement>("[data-booking-type]").forEach((b) => {
      b.classList.toggle("wiz-booking-type--on", b.dataset.bookingType === newValue);
    });
    // "Supply only" needs no date/time — keep the fields hidden for it.
    bookingFields.style.display = newValue && bookingNeedsDateTime(newValue) ? "" : "none";
    // …and retitle the card, since there's no appointment on that path.
    const titleEl = host.querySelector<HTMLElement>("[data-role=booking-title]");
    const subEl = host.querySelector<HTMLElement>("[data-role=booking-sub]");
    if (titleEl) {
      titleEl.innerHTML =
        `${escape(bookingHeading(newValue))} <span class="wiz-field__hint">(optional)</span>`;
    }
    if (subEl) subEl.textContent = bookingSubtitle(newValue);
  });
  bookingFields.querySelector<HTMLInputElement>("#wiz-date")!.addEventListener("change", (e) => {
    ctx.setState({
      ...ctx.state,
      booking: { ...ctx.state.booking, preferredDate: (e.target as HTMLInputElement).value },
    });
  });
  bookingFields.querySelector<HTMLSelectElement>("#wiz-time")!.addEventListener("change", (e) => {
    ctx.setState({
      ...ctx.state,
      booking: { ...ctx.state.booking, preferredTime: (e.target as HTMLSelectElement).value },
    });
  });
  }

  // ── GDPR ──
  const gdprBox = host.querySelector<HTMLElement>("[data-role=gdpr]")!;
  gdprBox.querySelector<HTMLInputElement>("input")!.addEventListener("change", (e) => {
    setProject(ctx, { gdprConsent: (e.target as HTMLInputElement).checked });
  });

  // ── Footer ──
  host.querySelector<HTMLButtonElement>('[data-action=back]')!
    .addEventListener("click", () => ctx.goTo(1));
  host.querySelector<HTMLButtonElement>('[data-action=next]')!
    .addEventListener("click", () => {
      if (validate(host, ctx)) ctx.goTo(3);
    });
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function pill(label: string, on: boolean): string {
  return `
    <label class="wiz-toggle ${on ? "wiz-toggle--on" : ""}" data-pill-value="${escapeAttr(label)}">
      <input type="radio" ${on ? "checked" : ""} /><span>${escape(label)}</span>
    </label>
  `;
}

function bindText(host: HTMLElement, sel: string, set: (v: string) => void): void {
  const el = host.querySelector<HTMLInputElement>(sel);
  if (!el) return;
  el.addEventListener("input", () => set(el.value));
}

function wirePillGroup(host: HTMLElement, groupSel: string, set: (v: string) => void): void {
  const group = host.querySelector<HTMLElement>(groupSel);
  if (!group) return;
  group.addEventListener("click", (e) => {
    const lbl = (e.target as HTMLElement).closest<HTMLElement>("[data-pill-value]");
    if (!lbl) return;
    const value = lbl.dataset.pillValue!;
    set(value);
    group.querySelectorAll<HTMLElement>("[data-pill-value]").forEach((el) => {
      el.classList.toggle("wiz-toggle--on", el === lbl);
    });
  });
}

function updateCustomer(ctx: StepContext, patch: Partial<CustomerInfo>): void {
  ctx.setState({ ...ctx.state, customer: { ...ctx.state.customer, ...patch } });
}

function setProject(ctx: StepContext, patch: Partial<{
  budget: BudgetChoice;
  timeline: TimelineChoice;
  leadSource: LeadSourceChoice;
  gdprConsent: boolean;
}>): void {
  ctx.setState({ ...ctx.state, project: { ...ctx.state.project, ...patch } });
}

function todayIso(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Lightweight UK postcode check — Royal Mail official regex, simplified.
 * Accepts AA9A 9AA / A9A 9AA / A9 9AA / A99 9AA / AA9 9AA / AA99 9AA with or
 * without the internal space (consumer input is forgiving). Server re-validates.
 */
function isLikelyUkPostcode(s: string): boolean {
  const t = s.toUpperCase().replace(/\s+/g, "");
  return /^(GIR0AA|[A-PR-UWYZ]([0-9]{1,2}|[A-HK-Y][0-9]([0-9ABEHMNPRV-Y])?|[0-9][A-HJKPS-UW])[0-9][ABD-HJLNP-UW-Z]{2})$/.test(t);
}

// ── Address lookup ───────────────────────────────────────────────────────────
// Postcode → selectable addresses via /api/public/address-lookup (server-side
// proxy; see modules/address_lookup.js). Every failure path ends in the manual
// field being revealed — a customer must never be unable to give us an address
// because a third-party API is having a bad day.

interface LookupAddress {
  line1: string;
  line2: string;
  town: string;
  county: string;
  postcode: string;
  label: string;
}

function wireAddressLookup(host: HTMLElement, ctx: StepContext): void {
  const postcodeEl = host.querySelector<HTMLInputElement>("#wiz-postcode")!;
  const findBtn = host.querySelector<HTMLButtonElement>("[data-role=find-address]")!;
  const resultsField = host.querySelector<HTMLElement>("[data-role=address-results-field]")!;
  const resultsEl = host.querySelector<HTMLSelectElement>("[data-role=address-results]")!;
  const manualField = host.querySelector<HTMLElement>("[data-role=address-manual-field]")!;
  const manualToggle = host.querySelector<HTMLButtonElement>("[data-role=address-manual-toggle]")!;
  const statusEl = host.querySelector<HTMLElement>("[data-role=address-status]")!;
  const addressEl = host.querySelector<HTMLInputElement>("#wiz-address")!;

  const setStatus = (msg: string, tone: "info" | "error" = "info"): void => {
    statusEl.textContent = msg;
    statusEl.classList.toggle("is-error", tone === "error");
    statusEl.classList.toggle("is-shown", !!msg);
  };

  const showManual = (focus: boolean): void => {
    manualField.hidden = false;
    manualToggle.hidden = true;
    if (focus) addressEl.focus();
  };

  manualToggle.addEventListener("click", () => showManual(true));
  // Already typed something (e.g. came back to this step) — keep it visible.
  if (ctx.state.customer.address) manualToggle.hidden = true;

  const find = async (): Promise<void> => {
    const postcode = postcodeEl.value.trim().toUpperCase();
    if (!postcode) { setStatus("Enter your postcode first, then tap Find address.", "error"); return; }
    if (!isLikelyUkPostcode(postcode)) { setStatus("That doesn't look like a valid UK postcode.", "error"); return; }

    findBtn.disabled = true;
    const originalLabel = findBtn.textContent;
    findBtn.textContent = "Finding…";
    setStatus("");
    try {
      const res = await fetch(`/api/public/address-lookup?postcode=${encodeURIComponent(postcode)}`, {
        credentials: "omit",
        headers: { Accept: "application/json" },
      });
      const body = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        addresses?: LookupAddress[];
        code?: string;
      };

      if (!res.ok || !body.ok) {
        // not_configured (no API key yet) and upstream failures are the same
        // story from the customer's side: type it in yourself.
        setStatus("We couldn't look that up — please type your address below.");
        showManual(true);
        return;
      }

      const addresses = Array.isArray(body.addresses) ? body.addresses : [];
      if (addresses.length === 0) {
        setStatus("No addresses found for that postcode — please type yours below.");
        showManual(true);
        return;
      }

      resultsEl.innerHTML =
        `<option value="">${escape(`${addresses.length} address${addresses.length === 1 ? "" : "es"} found — select yours`)}</option>` +
        addresses
          .map((a, i) => `<option value="${i}">${escape(a.label || a.line1)}</option>`)
          .join("");
      resultsField.hidden = false;
      setStatus("");

      resultsEl.onchange = (): void => {
        const idx = Number(resultsEl.value);
        const chosen = addresses[idx];
        if (!chosen) return;
        addressEl.value = chosen.label || chosen.line1;
        updateCustomer(ctx, {
          address: addressEl.value,
          postcode: chosen.postcode || postcode,
        });
        postcodeEl.value = chosen.postcode || postcode;
        // Show what was picked so it can be corrected — a lookup result is a
        // starting point (flat numbers, "The Annexe") not gospel.
        showManual(false);
        setStatus("Not quite right? Edit it above.");
      };
      resultsEl.focus();
    } catch {
      setStatus("We couldn't look that up — please type your address below.");
      showManual(true);
    } finally {
      findBtn.disabled = false;
      findBtn.textContent = originalLabel;
    }
  };

  findBtn.addEventListener("click", () => { void find(); });
  postcodeEl.addEventListener("keydown", (e) => {
    if (e.key === "Enter") { e.preventDefault(); void find(); }
  });
}

// ── Photo upload ─────────────────────────────────────────────────────────────

function wirePhotos(host: HTMLElement, ctx: StepContext): void {
  const drop = host.querySelector<HTMLLabelElement>("[data-role=photo-drop]")!;
  const input = host.querySelector<HTMLInputElement>("[data-role=photo-input]")!;
  const grid = host.querySelector<HTMLElement>("[data-role=photo-grid]")!;
  const err = host.querySelector<HTMLElement>('[data-err=photos]')!;

  function renderGrid(): void {
    grid.innerHTML = ctx.state.project.photos.map((p, i) => `
      <div class="wiz-photo-thumb">
        <img src="${escapeAttr(p.dataUrl)}" alt="${escapeAttr(p.name)}" />
        <button type="button" class="wiz-photo-thumb__remove" aria-label="Remove" data-photo-idx="${i}">×</button>
      </div>
    `).join("");
  }
  renderGrid();

  grid.addEventListener("click", (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>("[data-photo-idx]");
    if (!btn) return;
    const idx = Number(btn.dataset.photoIdx);
    const next = ctx.state.project.photos.filter((_, i) => i !== idx);
    setProject2(ctx, { photos: next });
    renderGrid();
  });

  input.addEventListener("change", async () => {
    const files = Array.from(input.files || []);
    input.value = "";
    err.textContent = "";
    const remaining = MAX_PHOTOS - ctx.state.project.photos.length;
    if (files.length === 0) return;
    if (remaining <= 0) {
      err.textContent = `Max ${MAX_PHOTOS} photos.`;
      return;
    }
    const accepted: PhotoUpload[] = [];
    for (const file of files.slice(0, remaining)) {
      if (!ACCEPTED_IMAGE.test(file.type)) continue;
      if (file.size > MAX_PHOTO_BYTES) {
        err.textContent = `${file.name} is over 5MB.`;
        continue;
      }
      try {
        const { dataUrl, sizeBytes } = await compressImage(file);
        accepted.push({ name: file.name, dataUrl, sizeBytes });
      } catch {
        // skip
      }
    }
    if (accepted.length === 0) return;
    setProject2(ctx, { photos: [...ctx.state.project.photos, ...accepted] });
    renderGrid();
  });

  // Drag & drop on the label (works on desktop browsers).
  drop.addEventListener("dragover", (e) => { e.preventDefault(); drop.style.borderColor = "var(--brand-primary)"; });
  drop.addEventListener("dragleave", () => { drop.style.borderColor = ""; });
  drop.addEventListener("drop", (e) => {
    e.preventDefault();
    drop.style.borderColor = "";
    if (!e.dataTransfer) return;
    const dt = new DataTransfer();
    for (const f of Array.from(e.dataTransfer.files)) dt.items.add(f);
    input.files = dt.files;
    input.dispatchEvent(new Event("change"));
  });
}

function setProject2(ctx: StepContext, patch: { photos: PhotoUpload[] }): void {
  ctx.setState({ ...ctx.state, project: { ...ctx.state.project, ...patch } });
}

function readDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result || ""));
    r.onerror = () => reject(new Error("read failed"));
    r.readAsDataURL(file);
  });
}

// Downscale + re-encode photos in the browser BEFORE they go into the submit
// payload. A 5MB phone photo becomes a few hundred KB JPEG, so a full 6-photo
// submission stays small and fast instead of blowing past the API body limit
// (which is what made the final step fail). Falls back to the raw data URL if
// the canvas pipeline is unavailable or the file isn't a raster image.
const MAX_IMAGE_EDGE = 1600;
const JPEG_QUALITY = 0.82;

async function compressImage(file: File): Promise<{ dataUrl: string; sizeBytes: number }> {
  const raw = await readDataUrl(file);
  if (!/^image\/(jpe?g|png|webp)$/i.test(file.type)) {
    return { dataUrl: raw, sizeBytes: file.size };
  }
  try {
    const img = await loadImage(raw);
    const scale = Math.min(1, MAX_IMAGE_EDGE / Math.max(img.width, img.height));
    const targetW = Math.max(1, Math.round(img.width * scale));
    const targetH = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = targetH;
    const cx = canvas.getContext("2d");
    if (!cx) return { dataUrl: raw, sizeBytes: file.size };
    cx.drawImage(img, 0, 0, targetW, targetH);
    const out = canvas.toDataURL("image/jpeg", JPEG_QUALITY);
    // Keep the original if compression somehow didn't help (tiny images).
    if (out.length >= raw.length) return { dataUrl: raw, sizeBytes: file.size };
    // base64 string length → approx decoded byte count.
    return { dataUrl: out, sizeBytes: Math.round((out.length - out.indexOf(",") - 1) * 0.75) };
  } catch {
    return { dataUrl: raw, sizeBytes: file.size };
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const im = new Image();
    im.onload = () => resolve(im);
    im.onerror = () => reject(new Error("image load failed"));
    im.src = src;
  });
}

// ── Validation ───────────────────────────────────────────────────────────────

function validate(host: HTMLElement, ctx: StepContext): boolean {
  const c = ctx.state.customer;
  let ok = true;
  const errs: Record<string, string> = {};

  if (!c.firstName.trim()) { errs.firstName = "Required"; ok = false; }
  if (!c.lastName.trim())  { errs.lastName  = "Required"; ok = false; }
  if (!c.email.trim() || !/^\S+@\S+\.\S+$/.test(c.email)) {
    errs.email = "Please enter a valid email"; ok = false;
  }
  if (!c.phone.trim() || c.phone.replace(/\D/g, "").length < 7) {
    errs.phone = "Please enter a valid phone number"; ok = false;
  }
  // Errors say only what's WRONG. The "helps us plan your delivery" style
  // rationale is helper copy, not a fault, and now sits in a neutral hint under
  // each field — red text should mean something is actually wrong.
  if (!c.address.trim()) {
    errs.address = "Please enter your address"; ok = false;
  }
  if (!c.postcode.trim()) {
    errs.postcode = "Please enter your postcode"; ok = false;
  } else if (!isLikelyUkPostcode(c.postcode)) {
    errs.postcode = "Please enter a valid UK postcode"; ok = false;
  }
  if (!ctx.state.project.gdprConsent) {
    errs.gdpr = "Please confirm before continuing"; ok = false;
  }

  // Clear all errors first, then set the new ones.
  host.querySelectorAll<HTMLElement>("[data-err]").forEach((el) => { el.textContent = ""; });
  for (const k of Object.keys(errs)) {
    const el = host.querySelector<HTMLElement>(`[data-err="${k}"]`);
    if (el) el.textContent = errs[k] ?? "";
  }

  if (!ok) {
    // Scroll to first error so mobile users see what's wrong.
    const first = host.querySelector<HTMLElement>("[data-err]:not(:empty)");
    if (first) first.scrollIntoView({ behavior: "smooth", block: "center" });
  }
  return ok;
}
